import analyticsService from './Analytics';
import {
  AppEvent,
  AutoRechargeScreenName,
  AutoRechargeToggle,
  BasePlanSelection,
  BillingCycle,
  DataPlanType,
  DataPurchaseProps,
  EVENT_PROPERTIES,
  PaymentType,
  PlanType,
  PopUpType,
  PostPurchasePopUpProps,
  ProductType,
  PurchaseScreenProps,
  PurchaseType,
  RetryFailureSelect,
  RetryType,
  SelectType,
  Skip4GSelect
} from './eventMapper';

export function purchaseScreenProps(p: PurchaseScreenProps) {
  return {
    [EVENT_PROPERTIES.FROM_SCREEN]: p.fromScreen,
    [EVENT_PROPERTIES.PURCHASE_TYPE]: p.purchaseType,
    [EVENT_PROPERTIES.PLAN_TYPE]: p.planType
  };
}

export function postPurchasePopUpProps(
  select: string,
  p: PostPurchasePopUpProps
) {
  return {
    Select: select,
    Plan: p.plan,
    [EVENT_PROPERTIES.PURCHASE_TYPE]: p.purchaseType,
    [EVENT_PROPERTIES.PURCHASE_PATH]: p.purchasePath
  };
}

export function dataAutoRechargeProps(
  autoRechargeEnabled?: boolean,
  autoRechargePlan?: string
) {
  return {
    [EVENT_PROPERTIES.AUTO_RECHARGE_ENABLED]: String(autoRechargeEnabled),
    [EVENT_PROPERTIES.AUTO_RECHARGE_PLAN]:
      autoRechargeEnabled && autoRechargePlan ? autoRechargePlan : 'N/A'
  };
}

// event functions

export function trackInitiatePurchase(props: PurchaseScreenProps) {
  analyticsService.trackEvent(
    AppEvent.INITIATE_PURCHASE,
    purchaseScreenProps(props)
  );
}

export function trackSkipPurchase(props: PurchaseScreenProps) {
  analyticsService.trackEvent(
    AppEvent.SKIP_PURCHASE,
    purchaseScreenProps(props)
  );
}

export function trackSkipTrial(planType: PlanType, reason: string) {
  analyticsService.trackEvent(AppEvent.SKIP_TRIAL, {
    Reason: reason,
    [EVENT_PROPERTIES.PLAN_TYPE]: planType
  });
}

export function trackSelectBillingCycle(
  billingCycle: BillingCycle,
  planType: PlanType
) {
  analyticsService.trackEvent(AppEvent.SELECT_BILLING_CYCLE, {
    [EVENT_PROPERTIES.BILLING_CYCLE]: billingCycle,
    [EVENT_PROPERTIES.PLAN_TYPE]: planType
  });
}

export function trackSelectPlan(planName: string) {
  analyticsService.trackEvent(AppEvent.SELECT_PLAN, {
    [EVENT_PROPERTIES.PLAN_NAME]: planName
  });
}

export function trackInitiatePlanPurchase(
  plan: string,
  purchaseType: PurchaseType
) {
  analyticsService.trackEvent(AppEvent.INITIATE_PLAN_PURCHASE, {
    Plan: plan,
    [EVENT_PROPERTIES.PURCHASE_TYPE]: purchaseType
  });
}

export function trackPaymentSelection(
  paymentType: PaymentType,
  planType: PlanType
) {
  analyticsService.trackEvent(AppEvent.PAYMENT_SELECTION, {
    [EVENT_PROPERTIES.PAYMENT_TYPE]: paymentType,
    [EVENT_PROPERTIES.PLAN_TYPE]: planType
  });
}

export function trackConfirmPurchase(
  plan: string,
  purchaseType: Exclude<PurchaseType, PurchaseType.REACTIVATE>,
  purchasePath: string
) {
  analyticsService.trackEvent(AppEvent.CONFIRM_PURCHASE, {
    Plan: plan,
    [EVENT_PROPERTIES.PURCHASE_TYPE]: purchaseType,
    [EVENT_PROPERTIES.PURCHASE_PATH]: purchasePath
  });
}

// after purchase popup
export function trackPurchaseActivationPopUp(
  select: SelectType.GO_HOME,
  props: PostPurchasePopUpProps
) {
  analyticsService.trackEvent(AppEvent.PURCHASE_ACTIVATION_POPUP, {
    ...postPurchasePopUpProps(select, props)
  });
}

export function trackPurchaseFailurePopUp(
  OKAY: SelectType,
  props: PostPurchasePopUpProps
) {
  analyticsService.trackEvent(AppEvent.PURCHASE_FAILURE_POPUP, {
    ...postPurchasePopUpProps(SelectType.OKAY, props)
  });
}

export function trackNoPurchaseNecessaryPopUp(
  popUpType: PopUpType,
  props: PostPurchasePopUpProps
) {
  analyticsService.trackEvent(AppEvent.NO_PURCHASE_NECESSARY_POPUP, {
    ...postPurchasePopUpProps(SelectType.GO_HOME, props),
    [EVENT_PROPERTIES.POPUP_TYPE]: popUpType
  });
}

export function trackYouAreCoveredPopUp(
  select: SelectType.VIEW_UPGRADE_OPTIONS | SelectType.KEEP_CURRENT_PLAN,
  props: PostPurchasePopUpProps
) {
  analyticsService.trackEvent(AppEvent.YOU_ARE_COVERED_POPUP, {
    ...postPurchasePopUpProps(select, props)
  });
}

export function trackPurchaseDowngradePopUp(
  select: SelectType.GO_BACK_TO_PLAN | SelectType.CONTINUE_DOWNGRADE
) {
  analyticsService.trackEvent(AppEvent.PURCHASE_DOWNGRADE_POPUP, {
    Select: select
  });
}

export function trackPlanReactivationPopUp(plan: string) {
  analyticsService.trackEvent(AppEvent.PLAN_REACTIVATION_POPUP, {
    Select: SelectType.GO_HOME,
    Plan: plan
  });
}

export function trackMaxCameraPopUp() {
  analyticsService.trackEvent(AppEvent.MAX_CAMERA_POPUP, {
    Select: SelectType.OKAY
  });
}

// webview nav buttons event

export function trackDismiss(screen: string) {
  analyticsService.trackEvent(AppEvent.DISMISS, { Screen: screen });
}
export function trackRefresh(screen: string) {
  analyticsService.trackEvent(AppEvent.REFRESH, { Screen: screen });
}
export function trackBack(screen: string) {
  analyticsService.trackEvent(AppEvent.BACK, { Screen: screen });
}

// 4g events

export function trackSkipPurchase4G(
  select: Skip4GSelect,
  purchaseType: Extract<
    PurchaseType,
    PurchaseType.TRIAL | PurchaseType.PURCHASE
  >
) {
  analyticsService.trackEvent(AppEvent.SKIP_PURCHASE_4G, {
    Select: select,
    [EVENT_PROPERTIES.PURCHASE_TYPE]: purchaseType
  });
}

export function trackSelectDataSubscriptionBillingCycle(
  billingCycle: BillingCycle
) {
  analyticsService.trackEvent(AppEvent.SELECT_DATA_SUBSCRIPTION_BILLING_CYCLE, {
    [EVENT_PROPERTIES.BILLING_CYCLE]: billingCycle
  });
}

export function trackAutoRechargeToggle(
  select: AutoRechargeToggle,
  screenName: AutoRechargeScreenName
) {
  analyticsService.trackEvent(AppEvent.AUTO_RECHARGE_TOGGLE, {
    Select: select,
    [EVENT_PROPERTIES.SCREEN]: screenName
  });
}

export function trackSelectDataSubscriptionPlan(planName: string) {
  analyticsService.trackEvent(AppEvent.SELECT_DATA_SUBSCRIPTION_PLAN, {
    [EVENT_PROPERTIES.PLAN_SELECTION]: planName
  });
}

export function trackSelectAutoDataRechargePlan(
  planName: string,
  screenName: AutoRechargeScreenName
) {
  analyticsService.trackEvent(AppEvent.SELECT_AUTO_DATA_RECHARGE_PLAN, {
    [EVENT_PROPERTIES.PLAN_SELECTION]: planName,
    [EVENT_PROPERTIES.SCREEN]: screenName
  });
}

export function trackInitiateDataSubscriptionPurchase(
  plan: string,
  autoRechargeEnabled: boolean,
  purchaseType: Extract<
    PurchaseType,
    PurchaseType.PURCHASE | PurchaseType.UPGRADE | PurchaseType.TRIAL
  >,
  autoRechargePlan?: string
) {
  analyticsService.trackEvent(AppEvent.INITIATE_DATA_SUBSCRIPTION_PURCHASE, {
    Plan: plan,
    ...dataAutoRechargeProps(autoRechargeEnabled, autoRechargePlan),
    [EVENT_PROPERTIES.PURCHASE_TYPE]: purchaseType
  });
}

export function trackRejectDataSubscriptionPurchase() {
  analyticsService.trackEvent(AppEvent.REJECT_DATA_SUBSCRIPTION_PURCHASE, {});
}

export function trackConfirmDataPurchase(
  props: DataPurchaseProps,
  autoRechargePlan?: string
) {
  analyticsService.trackEvent(AppEvent.CONFIRM_DATA_PURCHASE, {
    Plan: props.plan,
    ...dataAutoRechargeProps(props.autoRechargeEnabled, autoRechargePlan),
    [EVENT_PROPERTIES.PURCHASE_TYPE]: props.purchaseType,
    [EVENT_PROPERTIES.PURCHASE_PATH]: props.purchasePath
  });
}

export function trackDataPurchaseActivationPopUp(
  props: DataPurchaseProps & { planType: DataPlanType },
  autoRechargePlan?: string
) {
  analyticsService.trackEvent(AppEvent.DATA_PURCHASE_ACTIVATION_POPUP, {
    Select: SelectType.GO_HOME,
    Plan: props.plan,
    ...dataAutoRechargeProps(props.autoRechargeEnabled, autoRechargePlan),
    [EVENT_PROPERTIES.PURCHASE_TYPE]: props.purchaseType,
    [EVENT_PROPERTIES.PURCHASE_PATH]: props.purchasePath,
    [EVENT_PROPERTIES.PLAN_TYPE]: props.planType
  });
}

export function trackDataPurchaseFailurePopUp(
  props: DataPurchaseProps & { planType: DataPlanType },
  autoRechargePlan?: string
) {
  analyticsService.trackEvent(AppEvent.DATA_PURCHASE_FAILURE_POPUP, {
    Select: SelectType.OKAY,
    Plan: props.plan,
    ...dataAutoRechargeProps(props.autoRechargeEnabled, autoRechargePlan),
    [EVENT_PROPERTIES.PURCHASE_TYPE]: props.purchaseType,
    [EVENT_PROPERTIES.PURCHASE_PATH]: props.purchasePath,
    [EVENT_PROPERTIES.PLAN_TYPE]: props.planType
  });
}

export function trackSelectOneTimeDataPlan(planName: string) {
  analyticsService.trackEvent(AppEvent.SELECT_ONE_TIME_DATA_PLAN, {
    [EVENT_PROPERTIES.PLAN_SELECTION]: planName
  });
}

// Quantity defaults to 1; becomes 2 if user already has an active one-time pass.
export function trackInitiateOneTimeDataPurchase(
  plan: string,
  quantity: number = 1
) {
  analyticsService.trackEvent(AppEvent.INITIATE_ONE_TIME_DATA_PURCHASE, {
    Plan: plan,
    [EVENT_PROPERTIES.QUANTITY_OF_PLAN]: quantity
  });
}

export function trackConfirmOneTimeDataPurchase(
  plan: string,
  quantity: number,
  purchasePath: string
) {
  analyticsService.trackEvent(AppEvent.CONFIRM_ONE_TIME_DATA_PURCHASE, {
    Plan: plan,
    [EVENT_PROPERTIES.QUANTITY_OF_PLAN]: quantity,
    [EVENT_PROPERTIES.PURCHASE_PATH]: purchasePath
  });
}

export function trackConfirmAutoRecharge(plan: string) {
  analyticsService.trackEvent(AppEvent.CONFIRM_AUTO_RECHARGE, {
    [EVENT_PROPERTIES.SCREEN]: AutoRechargeScreenName.SMART_AUTO_DATA_RECHARGE,
    Plan: plan
  });
}

export function trackAutoRechargeConfirmationPopUp(
  select:
    | SelectType.GO_HOME
    | SelectType.DISABLE_AUTO_RECHARGE
    | SelectType.KEEP_AUTO_RECHARGE,
  purchaseType?: AutoRechargeToggle
) {
  analyticsService.trackEvent(AppEvent.AUTO_RECHARGE_CONFIRMATION_POPUP, {
    Select: select,
    [EVENT_PROPERTIES.PURCHASE_TYPE]: purchaseType
  });
}

export function trackChooseBasePlan(planSelection: BasePlanSelection) {
  analyticsService.trackEvent(AppEvent.CHOOSE_BASE_PLAN, {
    [EVENT_PROPERTIES.PLAN_SELECTION]: planSelection
  });
}

export function trackConfirmBasePlan(planSelection: BasePlanSelection) {
  analyticsService.trackEvent(AppEvent.CONFIRM_BASE_PLAN, {
    [EVENT_PROPERTIES.PLAN_SELECTION]: planSelection,
    Select: SelectType.CONTINUE_WITH_PLAN
  });
}

// FCM-2514 retry-payment webview events.

// Fired when the user taps "Retry payment", once the charge resolves.
// `fromScreen` is the raw `user_path` value the app put on the webview URL,
// forwarded verbatim as the From-Screen dimension.
export function trackPaymentRetry(
  fromScreen: string,
  retryType: RetryType,
  planType: PlanType,
  isSuccessful: boolean
) {
  analyticsService.trackEvent(AppEvent.PAYMENT_RETRY, {
    [EVENT_PROPERTIES.FROM_SCREEN]: fromScreen,
    [EVENT_PROPERTIES.RETRY_TYPE]: retryType,
    [EVENT_PROPERTIES.PLAN_TYPE]: planType,
    [EVENT_PROPERTIES.IS_SUCCESSFUL]: isSuccessful
  });
}

// Fired for the action taken on the pop-up shown after a retry fails.
export function trackPaymentRetryFailureAction(select: RetryFailureSelect) {
  analyticsService.trackEvent(AppEvent.PAYMENT_RETRY_FAILURE_ACTION, {
    [EVENT_PROPERTIES.SELECT]: select
  });
}

// Fired when the user taps "Use a different card" on the landing screen.
export function trackTapUseDifferentCard(
  retryType: RetryType,
  planType: PlanType
) {
  analyticsService.trackEvent(AppEvent.TAP_USE_DIFFERENT_CARD, {
    [EVENT_PROPERTIES.RETRY_TYPE]: retryType,
    [EVENT_PROPERTIES.PLAN_TYPE]: planType
  });
}

// Fired when the user adds a new card and re-attempts payment, once it resolves.
export function trackPaymentRetryWithUpdatedCard(
  retryType: RetryType,
  planType: PlanType,
  isSuccessful: boolean
) {
  analyticsService.trackEvent(AppEvent.PAYMENT_RETRY_WITH_UPDATED_CARD, {
    [EVENT_PROPERTIES.RETRY_TYPE]: retryType,
    [EVENT_PROPERTIES.PLAN_TYPE]: planType,
    [EVENT_PROPERTIES.IS_SUCCESSFUL]: isSuccessful
  });
}

// Fired for the action taken on the pop-up shown after an updated-card
// payment fails.
export function trackUpdatedCardFailureAction(select: RetryFailureSelect) {
  analyticsService.trackEvent(AppEvent.UPDATED_CARD_FAILURE_ACTION, {
    [EVENT_PROPERTIES.SELECT]: select
  });
}

export function trackScreenView(
  screen: string,
  productType?: ProductType
): void {
  const props = productType
    ? {
        [EVENT_PROPERTIES.SCREEN_NAME]: screen,
        [EVENT_PROPERTIES.PRODUCT_TYPE]: productType
      }
    : { [EVENT_PROPERTIES.SCREEN_NAME]: screen };
  analyticsService.trackEvent(AppEvent.SCREEN_VIEW, props);
}
